import { useMemo, useState } from "react";
import { buildMaintenanceProfile, type MaintenanceContext } from "@/lib/maintenance";
import { buildMaintenanceYearRows } from "@/lib/maintenance-years";

const miles = (n: number) => `${n.toLocaleString()} mi`;

/**
 * Year-qualified maintenance answers on the canonical model URL. The year
 * selector is pure client state: it never changes the URL, so the schedule
 * has exactly one indexable address instead of one per model year.
 */
export function MaintenanceYearPanel({
  vehicles,
  fullName,
  ctx = {},
  year = null,
}: {
  vehicles: any[];
  fullName: string;
  makeSlug?: string | undefined;
  modelSlug?: string | undefined;
  ctx?: MaintenanceContext;
  /** Optional initial year; no longer read from the URL. */
  year?: number | null;
  linkableYears?: number[] | undefined;
}) {
  const rows = useMemo(() => buildMaintenanceYearRows(vehicles, ctx), [vehicles, ctx]);
  const [pickedYear, setPickedYear] = useState<number | null>(year);
  const selected = rows.find((r) => r.year === pickedYear) ?? rows[0];

  const yearProfile = useMemo(() => {
    if (!selected) return null;
    return buildMaintenanceProfile(
      vehicles.filter((v) => v.year === selected.year),
      ctx,
    );
  }, [vehicles, ctx, selected?.year]);

  if (!rows.length || !selected || !yearProfile) return null;

  return (
    <section className="border-t border-border p-4 sm:p-5">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
        <h3 className="font-semibold text-foreground">
          {selected.year} {fullName} maintenance schedule
        </h3>
        <label className="text-sm text-muted-foreground">
          Model year{" "}
          <select
            className="ml-1 rounded-md border border-border bg-background px-2 py-1 text-foreground"
            value={selected.year}
            onChange={(e) => setPickedYear(Number(e.target.value))}
          >
            {rows.map((r) => (
              <option key={r.year} value={r.year}>
                {r.year}
              </option>
            ))}
          </select>
        </label>
      </div>

      <p className="text-sm text-foreground mb-4">
        The {selected.year} {fullName} needs{" "}
        {yearProfile.isElectric ? (
          <>no oil changes; rotate tires every {miles(selected.rotationMiles)}</>
        ) : (
          <>
            an oil &amp; filter change every{" "}
            <strong className="text-primary">{miles(selected.oilIntervalMiles)}</strong> or
            12 months and tire rotation every {miles(selected.rotationMiles)}
          </>
        )}
        . Powertrain: {selected.powertrain}
        {selected.transmission ? ` · ${selected.transmission}` : ""}.
        {selected.changeNote ? ` Changed from the prior year: ${selected.changeNote}.` : ""}
      </p>

      <div className="overflow-x-auto rounded-lg border border-border">
        <table className="w-full text-sm">
          <thead className="bg-secondary/50 text-left">
            <tr>
              <th className="px-4 py-2 font-semibold">Year</th>
              <th className="px-4 py-2 font-semibold">Oil change</th>
              <th className="px-4 py-2 font-semibold">Tire rotation</th>
              <th className="px-4 py-2 font-semibold">Powertrain</th>
              <th className="px-4 py-2 font-semibold">Change vs. prior year</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr
                key={r.year}
                className={`border-t border-border ${r.year === selected.year ? "bg-secondary/30" : ""}`}
              >
                <td className="px-4 py-2 font-medium text-foreground">
                  {r.year !== selected.year ? (
                    <button
                      type="button"
                      onClick={() => setPickedYear(r.year)}
                      className="hover:text-primary"
                    >
                      {r.year}
                    </button>
                  ) : (
                    r.year
                  )}
                </td>
                <td className="px-4 py-2 text-primary font-medium whitespace-nowrap">
                  {r.oilIntervalMiles > 0 ? miles(r.oilIntervalMiles) : "None"}
                </td>
                <td className="px-4 py-2 whitespace-nowrap">{miles(r.rotationMiles)}</td>
                <td className="px-4 py-2 text-muted-foreground">{r.powertrain}</td>
                <td className="px-4 py-2 text-muted-foreground">{r.changeNote ?? "No change"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}
